import React, { useState, useContext, useRef } from 'react';
import { Formik, Form, Field } from 'formik';
import MaskedInput from 'react-text-mask';
import { createNumberMask } from 'text-mask-addons';

import { FloatingInput, InfoTray, DateRangeFilter, FilterCriteriaRow } from 'src/components';
import { formatCurrencyAmountSimple } from 'src/helpers/formatting';

import { MiniAppFocusWrapper } from 'src/components/miniapp-focus-wrapper/miniapp-focus-wrapper';
import MiniAppEvents from 'src/helpers/miniapp-events';
import { TransactionFilterContext } from '../../context/transaction-filter-context';
import { TIME_RANGE, setTimesOnSelectedDates, isSameDayDateRange } from './time-range-helpers';
import TealiumEvents from 'src/helpers/tealium-events';

const REFERENCE_FILTER_TYPE = 'reference';
const AMOUNT_FILTER_TYPE = 'amount';
const inMiniProgram = MiniAppEvents.isInMiniApp();
const filterPriceMask = createNumberMask({
  prefix: '',
  suffix: '',
  includeThousandsSeparator: false,
  thousandsSeparatorSymbol: ' ',
  allowDecimal: true,
  decimalSymbol: '.',
  decimalLimit: 2,
  integerLimit: 9,
  allowNegative: false,
  allowLeadingZeroes: false,
  inputMode: 'numeric',
});

export default function InfoTrayForm(props) {
  const [selectedFilterType, setSelectedFilterType] = useState(AMOUNT_FILTER_TYPE);
  const [oldTimeValues, setOldTimeValues] = useState([
    TIME_RANGE[0],
    TIME_RANGE[TIME_RANGE.length - 1],
  ]);
  const { transactionFilter, setTransactionFilter } = useContext(TransactionFilterContext);

  const ref = useRef(null);

  const onFormChange = (values) => {
    let transactionSearchVariables = {};

    if (selectedFilterType === REFERENCE_FILTER_TYPE) {
      transactionSearchVariables = { reference: values.reference.trim() };
    }

    if (selectedFilterType === AMOUNT_FILTER_TYPE) {
      transactionSearchVariables = {
        fromAmount: values.fromAmount,
        toAmount: values.toAmount,
      };
    }

    setTransactionFilter({
      ...transactionFilter,
      transactionSearch: {
        filterType: selectedFilterType,
        ...transactionSearchVariables,
      },
    });
  };

  const updateTime = (values) =>
    setTransactionFilter({
      ...transactionFilter,
      selectedDates: setTimesOnSelectedDates(
        values.fromTime,
        values.toTime,
        transactionFilter.selectedDates,
      ),
    });

  const validateForm = (values) => {
    const errors = {};

    if (selectedFilterType === AMOUNT_FILTER_TYPE) {
      if (!values.fromAmount) errors.error = true;

      if (!values.toAmount) errors.error = true;
    } else if (selectedFilterType === REFERENCE_FILTER_TYPE) {
      if (values.reference === '') errors.error = true;
    }

    if (values.fromTime !== oldTimeValues[0] || values.toTime !== oldTimeValues[1]) {
      setOldTimeValues([values.fromTime, values.toTime]);
      updateTime(values);
    }

    return errors;
  };

  return (
    <InfoTray
      selected={props.search}
      setSelected={props.setSearch}
      hideCloseButton={props.isMobile ? false : true}
      gapTop={props.isMobile ? '10px' : '127px'}
      title={props.isMobile ? 'Filter your transactions' : 'Select search category'}
    >
      <Formik
        initialValues={{
          reference: transactionFilter.transactionSearch.reference || '',
          fromAmount: transactionFilter.transactionSearch.fromAmount || null,
          toAmount: transactionFilter.transactionSearch.toAmount || null,
          fromTime: TIME_RANGE[0],
          toTime: TIME_RANGE[TIME_RANGE.length - 1],
        }}
        validate={validateForm}
        validateOnChange={true}
        onSubmit={(values) => {
          if (selectedFilterType === AMOUNT_FILTER_TYPE) {
            props.callingPage === 'terminal'
              ? TealiumEvents.terminalTransactionsSearchByAmount()
              : TealiumEvents.accountTransactionsSearchByAmount();
          } else if (selectedFilterType === REFERENCE_FILTER_TYPE) {
            props.callingPage === 'terminal'
              ? TealiumEvents.terminalTransactionsSearchByReference()
              : TealiumEvents.accountTransactionsSearchByReference();
          }

          onFormChange(values);

          props.setSearch(false);
        }}
      >
        {({ resetForm, values, errors }) => (
          <Form className="info-tray-form" style={{ height: '100%' }}>
            <FilterCriteriaRow
              className="is-hidden-tablet"
              showDate={true}
              filterCriteria={[
                {
                  value:
                    transactionFilter.transactionSearch.fromAmount &&
                    transactionFilter.transactionSearch.toAmount
                      ? `R${formatCurrencyAmountSimple(
                          transactionFilter.transactionSearch.fromAmount,
                        )} - R${formatCurrencyAmountSimple(
                          transactionFilter.transactionSearch.toAmount,
                        )}`
                      : null,
                  clear: () => {
                    setTransactionFilter({
                      ...transactionFilter,
                      transactionSearch: {
                        filterType: selectedFilterType,
                      },
                    });

                    resetForm({
                      values: { reference: '', fromAmount: null, toAmount: null },
                    });
                  },
                },
                {
                  value: transactionFilter?.transactionSearch?.reference,
                  clear: () => {
                    setTransactionFilter({
                      ...transactionFilter,
                      transactionSearch: {
                        filterType: selectedFilterType,
                      },
                    });

                    resetForm({
                      values: { reference: '', fromAmount: null, toAmount: null },
                    });
                  },
                },
              ]}
            />
            <div className="is-hidden-tablet">
              <p className="has-margin-bottom-7">Date</p>
              <DateRangeFilter />
            </div>

            {props.callingPage === 'terminal' && (
              <>
                <p className="is-hidden-tablet has-margin-y-7">Time</p>

                <div className="is-hidden-tablet columns is-inline-flex">
                  <div className="column time-filter is-narrow">
                    <Field
                      className="mobile-tab-design"
                      name="fromTime"
                      type="text"
                      placeholder="Time from"
                      component={MiniAppFocusWrapper}
                      innerComponent={FloatingInput}
                      items={
                        isSameDayDateRange(transactionFilter)
                          ? TIME_RANGE.slice(0, TIME_RANGE.length - 1)
                          : TIME_RANGE
                      }
                      getItemDisplayValue={(item) => item}
                      fieldType="select"
                      isDark={true}
                      hasNoAfterLine={true}
                      automationIdPrefix="From_Time_filter"
                      onItemSelect={(item) => TealiumEvents.transactionsTimeFilter(true, item)}
                    />
                    <div className="floating-dash">-</div>
                  </div>
                  <div className="column is-narrow time-filter">
                    <Field
                      className="mobile-tab-design"
                      name="toTime"
                      type="text"
                      placeholder="Time to"
                      component={MiniAppFocusWrapper}
                      innerComponent={FloatingInput}
                      items={
                        isSameDayDateRange(transactionFilter)
                          ? TIME_RANGE.slice(TIME_RANGE.indexOf(values.fromTime) + 1)
                          : TIME_RANGE
                      }
                      getItemDisplayValue={(item) => item}
                      fieldType="select"
                      isDark={true}
                      hasNoAfterLine={true}
                      automationIdPrefix="To_Time_filter"
                      onItemSelect={(item) => TealiumEvents.transactionsTimeFilter(false, item)}
                    />
                  </div>
                </div>
              </>
            )}

            <p className="is-hidden-tablet has-margin-y-7">Select search category</p>

            <div className="columns is-mobile is-gapless filter-types-box">
              <div className="column is-two-fifths">
                <button
                  className={`search-category-button ${
                    selectedFilterType === AMOUNT_FILTER_TYPE ? 'category-selected' : ''
                  }`}
                  onClick={() => {
                    resetForm({
                      values: {
                        reference: '',
                        fromAmount: null,
                        toAmount: null,
                        fromTime: oldTimeValues[0],
                        toTime: oldTimeValues[1],
                      },
                    });
                    setSelectedFilterType(AMOUNT_FILTER_TYPE);
                  }}
                >
                  Amount
                </button>
              </div>
              <div className="column">
                <button
                  className={`search-category-button ${
                    selectedFilterType === REFERENCE_FILTER_TYPE ? 'category-selected' : ''
                  }`}
                  onClick={() => {
                    resetForm({
                      values: {
                        reference: '',
                        fromAmount: null,
                        toAmount: null,
                        fromTime: oldTimeValues[0],
                        toTime: oldTimeValues[1],
                      },
                    });
                    setSelectedFilterType(REFERENCE_FILTER_TYPE);
                  }}
                >
                  Reference number
                </button>
              </div>
            </div>
            <>
              {selectedFilterType === AMOUNT_FILTER_TYPE && (
                <div data-testid="transaction-search-amount-inputs">
                  <div className={'price-range-wrapper ' + (inMiniProgram ? 'margin-top-40' : '')}>
                    <p>From</p>
                    <div className="wrapper price-wrapper">
                      <p className="currency-symbol symbol-turnover">R</p>
                      <Field
                        name="fromAmount"
                        render={({ field }) => (
                          <MaskedInput
                            {...field}
                            ref={ref}
                            id="fromAmount"
                            mask={filterPriceMask}
                            placeholder="0.00"
                            className={`input modal-input`}
                          />
                        )}
                      />

                      <div className={`grey-line`} />
                    </div>
                  </div>

                  <div className="price-range-wrapper">
                    <p>To</p>
                    <div className="wrapper price-wrapper">
                      <p className="currency-symbol symbol-turnover">R</p>
                      <Field
                        name="toAmount"
                        render={({ field }) => (
                          <MaskedInput
                            {...field}
                            ref={ref}
                            id="toAmount"
                            mask={filterPriceMask}
                            placeholder="0.00"
                            className={`input modal-input`}
                          />
                        )}
                      />

                      <div className={`grey-line`} />
                    </div>
                  </div>
                </div>
              )}
              {selectedFilterType === REFERENCE_FILTER_TYPE && (
                <div id="reference-search-float-label">
                  <span className="reference-search-input-icon is-icon-6 icon-search"></span>
                  <Field
                    className="reference-search-input"
                    name="reference"
                    type="text"
                    placeholder="Reference number"
                    component={MiniAppFocusWrapper}
                    innerComponent={FloatingInput}
                    fieldType="standard"
                    isDark={true}
                  />
                </div>
              )}
            </>
            <div className="foot-note">
              Please note that a category needs to be selected for the search to be successful.
            </div>
            <div className="buttons-container columns is-mobile">
              <div className="column is-one-third">
                <button
                  className="search-reset-button"
                  type="submit"
                  data-testid="transaction-search-submit-button"
                  automationid="Transactions_Button_Search_Reset"
                  onClick={() => {
                    resetForm({
                      values: {
                        reference: '',
                        fromAmount: null,
                        toAmount: null,
                        fromTime: TIME_RANGE[0],
                        toTime: TIME_RANGE[TIME_RANGE.length - 1],
                      },
                    });

                    setTransactionFilter({
                      ...transactionFilter,
                      transactionSearch: {},
                    });
                  }}
                >
                  Reset
                </button>
              </div>
              <div className="column">
                <button
                  className={`search-submit-button ${
                    Object.keys(errors).length === 0 ? 'form-valid' : ''
                  }`}
                  type="submit"
                  data-testid="transaction-search-submit-button"
                  automationid="Transactions_Button_Search_Submit"
                >
                  {props.isMobile ? 'Filter' : 'Search'}
                </button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </InfoTray>
  );
}
